import type { OccasionType } from './types';

export interface SaveOccasionResult {
  succeeded: OccasionType[];
  skipped: OccasionType[];
  failed: Array<{ occasion: OccasionType; error: unknown }>;
}

/**
 * Iterate over occasions and save each, tolerating per-occasion failures.
 *
 * The updateStore GraphQL mutation requires the store to already have the
 * occasion configured. If it doesn't, the backend returns 404 — we silently
 * skip that occasion rather than treating it as a save failure, because the
 * user's action (e.g. applying an hours template) legitimately targets only
 * the occasions the store has.
 *
 * Any other error is collected. The helper throws only if no occasion
 * succeeded and at least one failed, so a partial success closes the dialog
 * cleanly instead of surfacing a 500 banner after a successful write.
 */
export async function saveHoursForOccasions<T>(
  occasions: Array<[OccasionType, T]>,
  save: (occasion: OccasionType, data: T) => Promise<unknown>
): Promise<SaveOccasionResult> {
  const result: SaveOccasionResult = { succeeded: [], skipped: [], failed: [] };

  for (const [occasion, data] of occasions) {
    try {
      await save(occasion, data);
      result.succeeded.push(occasion);
    } catch (error) {
      if (isOccasionNotConfiguredError(error)) {
        result.skipped.push(occasion);
      } else {
        result.failed.push({ occasion, error });
      }
    }
  }

  if (result.succeeded.length === 0 && result.failed.length > 0) {
    const first = result.failed[0];
    throw first.error instanceof Error
      ? first.error
      : new Error(String(first.error));
  }

  return result;
}

function isOccasionNotConfiguredError(error: unknown): boolean {
  const err = error as {
    response?: { status?: number; data?: { error?: string } };
  };
  return (
    err?.response?.status === 404 && err?.response?.data?.error === 'Occasion not found'
  );
}
